import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, of, switchMap, withLatestFrom } from 'rxjs';
import { ImpersonationService } from '@niko-crm/ui';
import { extractError } from '../helpers/error-reader';
import { ImpersonationSessionActions } from './impersonation-session.actions';
import { selectImpersonationSessionQuery } from './impersonation-session.selectors';

/**
 * Admin side of the impersonation sessions (BR-AUTH-03). Calls
 * `ImpersonationService` — it already owns every `/auth/impersonate*` route —
 * rather than `AdminService`, which is grouped by admin resource (tenants,
 * roles, api keys) and has no auth-session concerns besides `select-tenant`.
 */
@Injectable()
export class ImpersonationSessionEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly impersonationService = inject(ImpersonationService);

  loadSessions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImpersonationSessionActions.loadSessions),
      switchMap(({ query }) =>
        this.impersonationService.listSessions(query).pipe(
          map((result) => ImpersonationSessionActions.loadSessionsSuccess({ result })),
          catchError((err) =>
            of(ImpersonationSessionActions.loadSessionsFailure({ error: extractError(err) })),
          ),
        ),
      ),
    ),
  );

  revokeSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImpersonationSessionActions.revokeSession),
      switchMap(({ id }) =>
        this.impersonationService.revokeSession(id).pipe(
          map(() => ImpersonationSessionActions.revokeSessionSuccess({ id })),
          catchError((err) =>
            of(ImpersonationSessionActions.revokeSessionFailure({ error: extractError(err) })),
          ),
        ),
      ),
    ),
  );

  /** After a revoke the current page is fetched again with the same filters. */
  reloadAfterRevoke$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ImpersonationSessionActions.revokeSessionSuccess),
      withLatestFrom(this.store.select(selectImpersonationSessionQuery)),
      map(([, query]) => ImpersonationSessionActions.loadSessions({ query })),
    ),
  );
}
